import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'

import { getFinancialProfile } from '@/selectors/user.selectors'
import { fetchUserDocuments } from '@/features/user/your-practice/documents/lib/userDocuments.slice'
import { useReselector, useScrollRef } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'

import { TextColors, TypographyStyles } from '@/styles'
import { Badge, Card, Heading, HeadingVariants, Text } from '@/ui'
import { YourBusiness } from './your-business'
import { TaxInformation } from './tax-information'
import { PersonalInformation } from './personal-information'

// TODO: Migrate to V2 skipcq: JS-0099
import { fetchUserDocumentCategoriesIfNeeded } from '@/featuresV1/Admin/UserDocumentCategories/userDocumentCategories.slice'
import { getTaxEntityName } from '@/featuresV1/Taxes/taxConstants'

export const PracticeProfile = () => {
  const dispatch = useAppDispatch()
  const fp = useReselector(getFinancialProfile)
  const taxEntityName = getTaxEntityName(fp?.taxEntityType)
  const [searchParams] = useSearchParams()
  const section = searchParams.get('section')

  const {
    scrollRef: yourBusinessScrollRef,
    scrollToRef: scrollToYourBusiness,
  } = useScrollRef()
  const {
    scrollRef: taxInformationScrollRef,
    scrollToRef: scrollToTaxInformation,
  } = useScrollRef()
  const {
    scrollRef: personalInformationScrollRef,
    scrollToRef: scrollToPersonalInformation,
  } = useScrollRef()

  // Scroll to sections based on query param
  useEffect(() => {
    if (section === 'business') {
      scrollToYourBusiness()
    } else if (section === 'tax') {
      scrollToTaxInformation()
    } else if (section === 'personal') {
      scrollToPersonalInformation()
    }
  }, [
    scrollToPersonalInformation,
    scrollToTaxInformation,
    scrollToYourBusiness,
    section,
  ])

  useEffect(() => {
    dispatch(fetchUserDocuments())
    dispatch(fetchUserDocumentCategoriesIfNeeded())
  }, [dispatch])

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:justify-between gap-2 px-4 mb-6">
        <div>
          <Heading variant={HeadingVariants.H1} className="!mb-0">
            Practice Profile
          </Heading>
          <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
            Update personal and business information used.
          </Text>
        </div>

        <Badge
          variant="default"
          color="blue"
          className="max-h-[32px] self-start sm:self-center"
        >
          {taxEntityName}
        </Badge>
      </div>

      <Card className="mb-6">
        <span ref={yourBusinessScrollRef} />
        <YourBusiness />
      </Card>

      <div className="flex flex-col md:flex-row gap-2 mb-12">
        <Card className="w-full md:w-1/2 mb-4">
          <span ref={taxInformationScrollRef} />
          <TaxInformation />
        </Card>

        <Card className="w-full md:w-1/2">
          <span ref={personalInformationScrollRef} />
          <PersonalInformation />
        </Card>
      </div>
    </>
  )
}
